import React from "react";
import { Lock, CheckCircle2 } from "lucide-react";

export const STEPS = [
  { id: 1, key: "agency",    title: "Agency & Client",         blurb: "Who's being served, by whom, and covered by what.",                                   eta: "≈ 60 sec",  etaHint: "Faster with a saved agency profile"  },
  { id: 2, key: "adl",       title: "ADL Assessment",          blurb: "Score the six Activities of Daily Living.",                                            eta: "≈ 90 sec",  etaHint: "One-tap quick-set patterns available" },
  { id: 3, key: "nursing",   title: "Nursing Tasks",           blurb: "Delegated acts, medications, incidental services.",                                    eta: "≈ 90 sec",  etaHint: "Checkboxes + PCP parameters"          },
  { id: 4, key: "comm",      title: "Communication & Senses",  blurb: "Communication, orientation, vision, hearing.",                                         eta: "≈ 30 sec",  etaHint: "Quickest section"                     },
  { id: 5, key: "medical",   title: "Medical History & Meds",  blurb: "Diagnoses and medications (with End Date).",                                           eta: "≈ 90 sec",  etaHint: "Autocomplete + photo→med-list capture" },
  { id: 6, key: "social",    title: "Social & Schedules",      blurb: "Social factors, activity + staffing grids.",                                           eta: "≈ 60 sec",  etaHint: "PCW schedule pre-populated"           },
  { id: 7, key: "nsa",       title: "Non-Skilled Assessment",  blurb: "Braden, Morse, nutrition, pain, continence, advance directives, caregiver burden.",   eta: "≈ 90 sec",  etaHint: "Voice dictation on every narrative"   },
  { id: 8, key: "cms485",    title: "Plan of Care",            blurb: "Cert period, DME, goals, orders — 1-page POC + CMS-485.",                              eta: "≈ 60 sec",  etaHint: "Cert period + DME quick-pick"         },
  { id: 9, key: "ai-review", title: "AI Clinical Review",      blurb: "Claude scans for missing/inconsistent data.",                                          eta: "≈ 30 sec",  etaHint: "Runs automatically when you arrive"   },
  { id: 10, key: "verify",   title: "Triple Verification",     blurb: "RN checklist + dual signatures → generate PDFs.",                                      eta: "≈ 60 sec",  etaHint: "13-item checklist + two signatures"   },
];

export function FieldLabel({ children, required }) {
  return (
    <span className="field-label">
      {children}
      {required && <span className="text-[#c05746] ml-0.5">*</span>}
    </span>
  );
}

export function TextInput({
  label, value, onChange, required, placeholder, type = "text",
  testid, hideLabel = false, compact = false, maxLength,
  prefilled = false, hint = "", locked = false, lockedHint = "",
  verified = false, verifiedLabel = "",
}) {
  const defaultLockHint = "Managed by Avida Care — contact the coordinator to update";
  const lockHint = locked ? (lockedHint || defaultLockHint) : "";
  const verifiedHint = verified
    ? `Verified from ${verifiedLabel || "last visit"} — toggle to update`
    : "";
  const hintText = hint || lockHint || verifiedHint
    || (prefilled ? "Pre-filled from Avida Care — please verify" : "");
  const inputBase = compact ? "tb-input h-9 py-1 text-[13px]" : "tb-input";
  // Lock takes precedence over verified (Avida-supplied fields stay
  // gray-locked even when the recert "update demographics" toggle is on).
  const inputClass = locked
    ? `${inputBase} bg-[#F1F5F9] border-[#D6E3EF] text-[#3A5275] cursor-not-allowed pr-9`
    : verified
    ? `${inputBase} bg-[#E9F5EE] border-[#B5D9C3] text-[#0E6B3F] cursor-not-allowed pr-9`
    : prefilled
    ? `${inputBase} bg-[#EEF5FC] border-[#9BBDDB]`
    : inputBase;
  const isReadonly = locked || verified;
  const StatusIcon = locked ? Lock : verified ? CheckCircle2 : null;
  const statusIconCls = locked ? "text-[#3A5275]" : "text-[#0E6B3F]";
  return (
    <label className="block w-full">
      {label && !hideLabel && (
        <FieldLabel required={required}>
          {label}
          {StatusIcon && (
            <StatusIcon
              className={`inline w-3 h-3 ml-1.5 -mt-0.5 ${statusIconCls}`}
              aria-label={locked ? "Locked — managed by Avida Care" : "Verified from last visit"}
            />
          )}
        </FieldLabel>
      )}
      <div className="relative">
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => { if (!isReadonly) onChange(e.target.value); }}
          placeholder={placeholder}
          className={inputClass}
          data-testid={testid}
          data-prefilled={prefilled ? "true" : undefined}
          data-locked={locked ? "true" : undefined}
          data-verified={verified ? "true" : undefined}
          aria-label={hideLabel ? label : undefined}
          aria-readonly={isReadonly || undefined}
          maxLength={maxLength}
          readOnly={isReadonly}
          tabIndex={isReadonly ? -1 : undefined}
        />
        {StatusIcon && (
          <StatusIcon
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none ${statusIconCls}`}
            aria-hidden="true"
          />
        )}
      </div>
      {hintText && (
        <span
          className={`block text-[11px] mt-0.5 italic ${
            locked ? "text-[#3A5275]" : verified ? "text-[#0E6B3F]" : "text-[#1565B8]"
          }`}
        >
          {hintText}
        </span>
      )}
    </label>
  );
}

export function TextArea({ label, value, onChange, placeholder, testid, rows = 3, locked = false, lockedHint = "" }) {
  const defaultLockHint = "Managed by Avida Care — contact the coordinator to update";
  const lockHint = locked ? (lockedHint || defaultLockHint) : "";
  const inputClass = locked
    ? "tb-textarea bg-[#F1F5F9] border-[#D6E3EF] text-[#3A5275] cursor-not-allowed"
    : "tb-textarea";
  return (
    <label className="block w-full">
      {label && (
        <FieldLabel>
          {label}
          {locked && (
            <Lock
              className="inline w-3 h-3 ml-1.5 -mt-0.5 text-[#3A5275]"
              aria-label="Locked — managed by Avida Care"
            />
          )}
        </FieldLabel>
      )}
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => { if (!locked) onChange(e.target.value); }}
        placeholder={placeholder}
        className={inputClass}
        data-testid={testid}
        data-locked={locked ? "true" : undefined}
        readOnly={locked}
        aria-readonly={locked || undefined}
        tabIndex={locked ? -1 : undefined}
      />
      {lockHint && (
        <span className="block text-[11px] text-[#3A5275] mt-0.5 italic">{lockHint}</span>
      )}
    </label>
  );
}

export function SelectInput({
  label, value, onChange, options, testid, required,
  hideLabel = false, compact = false, prefilled = false, hint = "",
  locked = false, lockedHint = "", verified = false, verifiedLabel = "",
}) {
  const defaultLockHint = "Managed by Avida Care — contact the coordinator to update";
  const lockHint = locked ? (lockedHint || defaultLockHint) : "";
  const verifiedHint = verified
    ? `Verified from ${verifiedLabel || "last visit"} — toggle to update`
    : "";
  const hintText = hint || lockHint || verifiedHint
    || (prefilled ? "Pre-filled from Avida Care — please verify" : "");
  const inputBase = compact ? "tb-input h-9 py-1 text-[13px]" : "tb-input";
  const inputClass = locked
    ? `${inputBase} bg-[#F1F5F9] border-[#D6E3EF] text-[#3A5275] cursor-not-allowed pr-9`
    : verified
    ? `${inputBase} bg-[#E9F5EE] border-[#B5D9C3] text-[#0E6B3F] cursor-not-allowed pr-9`
    : prefilled
    ? `${inputBase} bg-[#EEF5FC] border-[#9BBDDB]`
    : inputBase;
  const isDisabled = locked || verified;
  const StatusIcon = locked ? Lock : verified ? CheckCircle2 : null;
  const statusIconCls = locked ? "text-[#3A5275]" : "text-[#0E6B3F]";
  return (
    <label className="block w-full">
      {label && !hideLabel && (
        <FieldLabel required={required}>
          {label}
          {StatusIcon && (
            <StatusIcon
              className={`inline w-3 h-3 ml-1.5 -mt-0.5 ${statusIconCls}`}
              aria-label={locked ? "Locked — managed by Avida Care" : "Verified from last visit"}
            />
          )}
        </FieldLabel>
      )}
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={(e) => { if (!isDisabled) onChange(e.target.value); }}
          className={inputClass}
          data-testid={testid}
          data-prefilled={prefilled ? "true" : undefined}
          data-locked={locked ? "true" : undefined}
          data-verified={verified ? "true" : undefined}
          aria-label={hideLabel ? label : undefined}
          aria-readonly={isDisabled || undefined}
          disabled={isDisabled}
          tabIndex={isDisabled ? -1 : undefined}
        >
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </option>
          ))}
        </select>
        {StatusIcon && (
          <StatusIcon
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none ${statusIconCls}`}
            aria-hidden="true"
          />
        )}
      </div>
      {hintText && (
        <span
          className={`block text-[11px] mt-0.5 italic ${
            locked ? "text-[#3A5275]" : verified ? "text-[#0E6B3F]" : "text-[#1565B8]"
          }`}
        >
          {hintText}
        </span>
      )}
    </label>
  );
}

export function Grid({ cols = 2, children, className = "" }) {
  const c = { 1: "grid-cols-1", 2: "grid-cols-1 md:grid-cols-2", 3: "grid-cols-1 md:grid-cols-3", 4: "grid-cols-2 md:grid-cols-4" }[cols] || "grid-cols-1 md:grid-cols-2";
  return <div className={`grid ${c} gap-5 ${className}`}>{children}</div>;
}

export function SectionHeading({ eyebrow, title, description }) {
  return (
    <div className="mb-6">
      {eyebrow && <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] font-semibold mb-2">{eyebrow}</div>}
      <h2 className="text-2xl md:text-3xl font-display font-semibold text-[#0B1E3A]">{title}</h2>
      {description && <p className="text-[#3A5275] mt-1 text-[15px]">{description}</p>}
    </div>
  );
}
